import React, { FocusEventHandler, forwardRef, useCallback, useEffect, useMemo } from 'react'
import { useDebounce } from 'react-use'
import AutocompleteBase, {
  IAutocompleteBaseOption,
  TAutocompleteBaseChangeHandler,
  isAutocompleteBaseOption,
  useAutocompleteLazyQuery,
  useAutocompleteInputState,
  TInputChangeHandler,
  TAutocompleteBaseSharedProps,
} from 'transfix-ui/components/AutocompleteBase'
import { IShipperFragment } from './graphql/ShipperFragment'
import { useShippersLazyQuery } from './graphql/Shippers'

type TBaseValue = Pick<IAutocompleteBaseOption, 'id' | 'text'>

/**
 * @note Autocomplete always uses the `id` prop to match up options
 */
const getComputedValue = (
  options: IAutocompleteBaseOption[],
  valueProp: TBaseValue | null
): IAutocompleteBaseOption | null => {
  const { id: idProp } = valueProp || {}
  const option = options.find(({ id }) => id === idProp)
  return option || (valueProp as IAutocompleteBaseOption) || null
}

export interface IAutocompleteShipperProps<V extends TBaseValue = TBaseValue>
  extends TAutocompleteBaseSharedProps {
  /**
   * @example { id: '123', text: 'My Shipper' }
   */
  value: V | null
  /**
   * @note value and onChange should always match types, if possible
   */
  onChange: (newVal: V | null) => void
  dataTest?: string
  onBlur?: FocusEventHandler<HTMLInputElement>
}

const AutocompleteShipper = forwardRef<HTMLInputElement, IAutocompleteShipperProps>(
  (
    {
      dataTest = 'AutocompleteShipper',
      onChange,
      placeholder: placeholderProp = 'Shipper',
      value: valueProp,
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useAutocompleteInputState()
    const [refetch, { options = [], loading }] = useAutocompleteLazyQuery(
      useShippersLazyQuery,
      {},
      {
        getOptionsConfig: data => {
          const { searchShipperApplications } = data || {}
          const { nodes } = searchShipperApplications || {}
          return {
            options: nodes || [],
            groupIndex: 1,
            mapOption: ({ id, name, ...rest }: IShipperFragment) => ({
              id: String(id),
              text: name ?? '',
              /**
               * Include the rest of the data as an object to use in other parts of form state
               */
              data: rest,
            }),
          }
        },
      }
    )

    const changeHandler: TAutocompleteBaseChangeHandler = useCallback(
      (_, newVal) => {
        if (isAutocompleteBaseOption(newVal)) {
          onChange(newVal)
        } else {
          onChange(null)
        }
      },
      [onChange]
    )

    /**
     * Refetch when the input value changes
     */
    useDebounce(
      () => {
        if (inputValue) {
          refetch({ variables: { searchTerm: inputValue } })
        }
      },
      250,
      [inputValue]
    )

    const inputChangeHandler: TInputChangeHandler = useCallback(
      (e, newVal) => {
        /**
         * MUI triggers an onChange event with `null` value when
         * trying to press delete/backspace when there's a value
         *
         * Because this is an invalid event, we ignore it
         */
        if (e) {
          setInputValue(e, newVal)
        }
      },
      [setInputValue]
    )

    /**
     * Set input value by default
     */
    useEffect(() => {
      if (!inputValue) {
        setInputValue({} as any, valueProp?.text || '')
      }
    }, [inputValue, setInputValue, valueProp])

    const value = useMemo(() => getComputedValue(options, valueProp), [options, valueProp])

    return (
      <AutocompleteBase
        {...props}
        data-test={dataTest}
        ref={ref}
        value={value}
        onChange={changeHandler}
        options={options}
        loading={loading}
        placeholder={placeholderProp}
        loadingText='Loading...'
        onInputChange={inputChangeHandler}
      />
    )
  }
)

export default AutocompleteShipper
